import { useCallback, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface FixedPos {
  top: number
  left: number
}

function TooltipPortal({ label, pos, visible }: { label: string; pos: FixedPos | null; visible: boolean }) {
  if (!visible || !pos) return null
  return createPortal(
    <span
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translate(-50%, -100%)',
      }}
      className="pointer-events-none z-[200] w-56 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-center text-xs leading-relaxed text-white shadow-lg"
    >
      {label}
    </span>,
    document.body,
  )
}

interface Props {
  label: string | null
  children: ReactNode
  className?: string
}

/**
 * Tooltip nad elementem (portal) — działa też na disabled przyciskach i przy overflow.
 */
export function OnboardingLockedTooltip({ label, children, className = '' }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<FixedPos | null>(null)
  const [visible, setVisible] = useState(false)

  const show = useCallback(() => {
    if (!label || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    setPos({ top: r.top - 8, left: r.left + r.width / 2 })
    setVisible(true)
  }, [label])

  const hide = useCallback(() => setVisible(false), [])

  if (!label) {
    return <div className={className}>{children}</div>
  }

  return (
    <>
      <div
        ref={ref}
        className={className}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </div>
      <TooltipPortal label={label} pos={pos} visible={visible} />
    </>
  )
}
